"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hero" data-nav="light" className="relative w-full border-b border-border bg-background py-16 text-foreground md:py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-12">
        {/* Main Two-Column Layout */}
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          {/* Left Column: Primary Copy & Actions */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3
        }} className="lg:col-span-7">
            {/* Small Label */}
            <div className="inline-flex items-center gap-2 border border-border bg-muted px-3 py-1 font-mono text-xs uppercase tracking-widest text-muted-foreground">
              <span>SHIPPING • DELIVERY • ORDERING</span>
            </div>

            {/* Main H1 Headline */}
            <h1 className="mt-5 font-serif text-4xl font-bold uppercase tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Mechanical Bull Shipping, Delivery & Ordering
            </h1>

            {/* Primary Body Copy */}
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                Mechanical bulls are large equipment purchases, so delivery requires more
                planning than a normal parcel shipment. The shipping arrangement can
                depend on the exact machine, included equipment, packed size,
                destination and unloading access.
              </p>
              <p>
                Mechanical Bull Shop uses an enquiry-based ordering process so buyers can
                review the product, available delivery information and order details
                before proceeding.
              </p>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild className="h-auto border border-primary bg-primary px-6 py-3.5 font-serif text-base font-semibold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90">
                <Link to="/NewMechanicalBullsPage">
                  View Mechanical Bulls
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="h-auto border border-foreground bg-transparent px-6 py-3.5 font-serif text-base font-semibold uppercase tracking-wider text-foreground transition-colors hover:bg-foreground hover:text-background">
                <Link to="/ContactPage">
                  Ask About Delivery
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Industrial Spec & Delivery Overview Plate */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.3,
          delay: 0.1
        }} className="lg:col-span-5">
            <div className="relative border border-border bg-card p-6 sm:p-8">
              {/* Blueprint Corner Accents */}
              <div className="absolute top-0 left-0 -translate-x-[1px] -translate-y-[1px] h-2 w-2 border-t-2 border-l-2 border-primary" />
              <div className="absolute top-0 right-0 translate-x-[1px] -translate-y-[1px] h-2 w-2 border-t-2 border-r-2 border-primary" />
              <div className="absolute bottom-0 left-0 -translate-x-[1px] translate-y-[1px] h-2 w-2 border-b-2 border-l-2 border-primary" />
              <div className="absolute bottom-0 right-0 translate-x-[1px] translate-y-[1px] h-2 w-2 border-b-2 border-r-2 border-primary" />

              {/* Spec Plate Header */}
              <div className="flex items-center justify-between border-b border-border pb-3">
                <span className="font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  ORDERING CONSIDERATIONS
                </span>
                <span className="font-mono text-xs uppercase text-primary">
                  COMMERCIAL SALES
                </span>
              </div>

              {/* Specifications List */}
              <div className="mt-6 space-y-4 font-mono text-sm">
                <div className="border-b border-border/60 pb-3">
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Equipment Configuration
                  </span>
                  <span className="mt-1 block font-medium text-foreground">
                    Tailored to selected bull model and included gear
                  </span>
                </div>

                <div className="border-b border-border/60 pb-3">
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Packaging & Footprint
                  </span>
                  <span className="mt-1 block font-medium text-foreground">
                    Arranged according to machine and mat specifications
                  </span>
                </div>

                <div className="border-b border-border/60 pb-3">
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Destination & Unloading
                  </span>
                  <span className="mt-1 block font-medium text-foreground">
                    Planned around destination site conditions and access
                  </span>
                </div>

                <div>
                  <span className="block text-xs uppercase tracking-wider text-muted-foreground">
                    Procurement Model
                  </span>
                  <span className="mt-1 block font-medium text-foreground">
                    Enquiry-based review prior to order finalization
                  </span>
                </div>
              </div>

              {/* Informational Note */}
              <div className="mt-6 border border-border bg-muted p-4">
                <p className="font-sans text-xs leading-relaxed text-muted-foreground">
                  Our direct enquiry process ensures buyers have clear confirmation of
                  machine specifications, packed sizing, and delivery arrangements before any
                  order commitment.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}